import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDownload } from '@/hooks/useDownload';
import { ArtifactAuditStatus, Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import { treeStyles, TreeViewSize } from '../constants/treeStyles';
import { useDocumentTree } from '../context/DocumentTreeContext';
import { getFileIcon } from '../utils/fileIcons';
import { formatDateString, formatFileSize } from '../utils/fileUtils';
import { handleEventPropagation, isClickOnElement } from '../utils/nodeUtils';
import { BaseNodeItem } from './BaseNodeItem';
import { FileActions } from './FileActions';

interface FileNodeItemProps {
  item: BlobType;
  level: number;
  collectionTree: CollectionNode;
  className?: string;
  matchesSearch?: boolean;
  isTargeted?: boolean;
  isDraggingActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSuccess?: () => void;
  onSelect?: (e?: React.MouseEvent) => void;
  isSelected?: boolean;
  'aria-label'?: string;
  size?: TreeViewSize;
  /** Matter ID for sink action API calls */
  matterId?: string;
}

interface FileStatsProps {
  fileSize: number;
  fileExtension?: string;
  createdAt: string;
  size?: TreeViewSize;
}

/**
 * Formats the created_at date to a compact, readable format with time
 * Shows "Today, 3:45 PM", "Yesterday, 10:30 AM", or "Dec 3, 2:15 PM" for dates within the year,
 * and "Dec 3, 2024, 9:00 AM" for older dates
 */
const formatCompactDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const timeFormat = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const timeStr = timeFormat.format(date);

  if (dateOnly.getTime() === today.getTime()) {
    return `Today, ${timeStr}`;
  }
  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Yesterday, ${timeStr}`;
  }

  const isCurrentYear = date.getFullYear() === now.getFullYear();
  if (isCurrentYear) {
    const dateFormat = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${dateFormat.format(date)}, ${timeStr}`;
  }

  const dateFormat = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${dateFormat.format(date)}, ${timeStr}`;
};

const FileStats: React.FC<FileStatsProps> = ({
  fileSize,
  fileExtension,
  createdAt,
  size = 'sm',
}) => (
  <div
    className={`${treeStyles.stats[size]} text-muted-foreground whitespace-nowrap grid`}
  >
    {/*
     * Both spans occupy the same grid cell (row-1 col-1) so the container
     * width is always the max of the two.  Only one is visible at a time,
     * preventing layout shift when hovering.
     */}

    {/* Upload date - visible by default, hidden on hover */}
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="col-start-1 row-start-1 hidden @[240px]:inline-block group-hover:hidden text-muted-foreground/70 transition-opacity duration-150 text-right">
            {formatCompactDate(createdAt)}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="text-xs bg-popover border border-border shadow-lg"
        >
          <span className="font-medium">Uploaded:</span>{' '}
          {formatDateString(createdAt, 'full')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>

    {/* File size and extension - visible on hover */}
    <span className="col-start-1 row-start-1 hidden group-hover:inline text-right">
      {formatFileSize(fileSize)}
      {fileExtension && ` • ${fileExtension.toUpperCase().replace('.', '')}`}
    </span>
  </div>
);

interface FileActionsComponentProps {
  fileName: string;
  handleDownload: (e: React.MouseEvent) => void;
  file: BlobType;
  collectionTree: CollectionNode;
  onSuccess: () => void;
  size?: TreeViewSize;
}

const FileActionsComponent: React.FC<FileActionsComponentProps> = ({
  fileName,
  handleDownload,
  file,
  collectionTree,
  onSuccess,
  size = 'sm',
}) => (
  <>
    <button
      className="p-0.5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground file-download-button"
      onClick={handleDownload}
      title={`Download ${fileName}`}
      type="button"
    >
      <ArrowDownTrayIcon className={`${treeStyles.icons.action}`} />
    </button>
    <FileActions
      file={file}
      collectionTree={collectionTree}
      onSuccess={onSuccess}
      size={size}
    />
  </>
);

export const FileNodeItem: React.FC<FileNodeItemProps> = ({
  item: file,
  level,
  collectionTree,
  className,
  matchesSearch = false,
  isTargeted = false,
  isDraggingActive = false,
  onMouseEnter,
  onMouseLeave,
  onSuccess,
  onSelect,
  isSelected: propIsSelected,
  'aria-label': ariaLabel,
  size = 'sm',
  matterId,
}) => {
  const { isFileSelected, toggleFileSelection, setActiveFileAndSelect } =
    useDocumentTree();
  const { downloadFile } = useDownload();

  const isFileInSelection = isFileSelected(file.id);

  const isActive = propIsSelected !== undefined ? propIsSelected : false;

  const fileIcon = getFileIcon(file.file_extension, size);

  const handleClick = (e: React.MouseEvent) => {
    if (
      !isClickOnElement(e, 'file-actions') &&
      !isClickOnElement(e, 'file-download-button') &&
      !isClickOnElement(e, 'selection-indicator')
    ) {
      // Support Shift/Cmd/Ctrl click for multi-select
      const isMultiSelectModifier = e.shiftKey || e.metaKey || e.ctrlKey;

      if (isMultiSelectModifier && onSelect) {
        // Pass event to parent for range/toggle selection handling
        onSelect(e);
      } else if (isMultiSelectModifier) {
        // Fallback: toggle the file in selection without changing active
        toggleFileSelection(file.id);
      } else if (onSelect) {
        onSelect(e);
      } else {
        setActiveFileAndSelect(file.id);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    handleEventPropagation.keyboardSelect(e, () => {
      if (onSelect) {
        onSelect();
      } else {
        setActiveFileAndSelect(file.id);
      }
    });
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    downloadFile({
      downloadUrl: file.download_url,
      name: file.name,
      fileExtension: file.file_extension,
    });
  };

  const handleCheckboxChange = () => {
    toggleFileSelection(file.id);
  };

  const safeOnSuccess = () => {
    if (onSuccess) onSuccess();
  };

  const stats = (
    <FileStats
      fileSize={file.file_size}
      fileExtension={file.file_extension || undefined}
      createdAt={file.created_at}
      size={size}
    />
  );

  const actions = (
    <FileActionsComponent
      fileName={file.name}
      handleDownload={handleDownload}
      file={file}
      collectionTree={collectionTree}
      onSuccess={safeOnSuccess}
      size={size}
    />
  );

  // Get the most relevant sink action to display (prioritize FAILED)
  const getRelevantSinkAction = () => {
    const sinkActions = file.sink_actions ?? [];
    if (!sinkActions?.length) return undefined;

    // Otherwise return the first non-pushed action
    return sinkActions[0];
  };

  // Only show audit indicator for non-silent statuses
  const VISIBLE_AUDIT_STATUSES: ArtifactAuditStatus[] = [
    'PENDING',
    'RUNNING',
    'FLAGGED_PENDING_REVIEW',
    'FAILED',
  ];
  const auditData =
    file.artifact_audit &&
    VISIBLE_AUDIT_STATUSES.includes(file.artifact_audit.status)
      ? file.artifact_audit
      : undefined;

  return (
    <BaseNodeItem
      level={level}
      size={size}
      className={`${className} ${matchesSearch ? 'font-semibold' : ''} ${
        isTargeted && isDraggingActive
          ? 'bg-blue-50 transition-colors animate-pulse'
          : ''
      }`}
      isSelected={isActive}
      icon={fileIcon}
      label={file.name}
      selectable
      selectionChecked={isFileInSelection}
      onSelectionChange={handleCheckboxChange}
      stats={stats}
      actions={actions}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel || `File: ${file.name}`}
      dataBlobParentId={file.collection_details.id}
      sinkAction={getRelevantSinkAction()}
      matterId={matterId}
      auditData={auditData}
      artifactId={file.artifact_id || undefined}
    />
  );
};
